import { createCacheKey, ENDPOINTS, useCreatePayload } from 'hammock';
import React, { useCallback, useEffect, useState } from 'react';
import { useQueryClient } from 'react-query';

import ValidationErrorManager from 'commons/containers/ValidationErrorManager';
import { ContentDocument } from 'craft/types';
import {
  alertTemplate,
  baseStylesheet,
  defaultInlineDocument,
  enabledBlockTemplates,
  schema,
  styleguideVariants,
} from 'launchpad/constants';

import LaunchpadCraft from './LaunchpadCraft';
import { LaunchpadStylesheet } from './LaunchpadStylesheet';

interface LaunchpadEditorProps {
  /**
   * API errors from parent fetcher
   */
  error?: any;
}

const useLaunchpadMocks = () => {
  // it puts the schema in the cache, selectors.ts uses this
  const queryClient = useQueryClient();

  const [mocked, setMocked] = useState(false);

  const { get: getSchemas } = useCreatePayload(ENDPOINTS.schemas);
  const { getAll: getVariants } = useCreatePayload(ENDPOINTS.styleguideVariants);
  const { getAll: getTemplateMetadata } = useCreatePayload(ENDPOINTS.templateMetadata);
  const { get: getAlertTemplate } = useCreatePayload(ENDPOINTS.templateDocumentsEditor);
  const { getAll: getCollections } = useCreatePayload(ENDPOINTS.collections);
  const { get: getStyleguideProcessor } = useCreatePayload(ENDPOINTS.styleguideProcessor);
  const { getAll: getFontFamilies } = useCreatePayload(ENDPOINTS.fontFamilies);

  useEffect(() => {
    queryClient.setQueryData(createCacheKey(getSchemas({ id: 'candu' })), schema);
    queryClient.setQueryData(createCacheKey(getVariants({ query: {} })), styleguideVariants);

    queryClient.setQueryData(
      createCacheKey(getTemplateMetadata({ query: { finder: 'byEnabledBlock' } })),
      enabledBlockTemplates,
    );

    queryClient.setQueryData(createCacheKey(getAlertTemplate({ id: '523' })), alertTemplate);
    queryClient.setQueryData(createCacheKey(getCollections({ query: {} })), []);

    queryClient.setQueryData(
      createCacheKey(getStyleguideProcessor({ id: 'base-stylesheet.json' })),
      baseStylesheet,
    );

    queryClient.setQueryData(createCacheKey(getFontFamilies({ query: {} })), []);

    setMocked(true);
  }, []);

  return { mocked };
};

export const LaunchpadEditor = React.memo(({ error }: LaunchpadEditorProps) => {
  const onChange = useCallback((payload: ContentDocument) => {
    console.log(payload);
  }, []);

  const { mocked } = useLaunchpadMocks();

  const apiErrors = error?.details?.errors || [];

  // don't render the editor if the mocks are not set
  if (!mocked) {
    return <></>;
  }

  return (
    <ValidationErrorManager
      errors={[...apiErrors]}
      apiErrorMessage={
        error?.errorCode !== 'SCHEMA_VALIDATION_FAILED_USER_INPUT' ? error?.message : ''
      }
    >
      <LaunchpadStylesheet>
        <LaunchpadCraft
          document={defaultInlineDocument}
          schema={schema}
          onChange={onChange}
          actionBar={<>actionBar</>}
          mode="edit"
        />
      </LaunchpadStylesheet>
    </ValidationErrorManager>
  );
});
